/**
 * Parses the response from the Supabase function
 * @param response The response from the Supabase function
 * @returns A Promise containing the parsed response
 */
export const parseNetlifyFunctionResponse = async (response: Response) => {
  // Check if the response is ok
  if (!response.ok) {
    // Try to get the error as text first
    const errorText = await response.text();
    
    console.error("Response status:", response.status);
    console.error("Response headers:", JSON.stringify(Array.from(response.headers.entries())));
    console.error("Response error text (first 300 chars):", errorText.substring(0, 300));
    
    // Detailed environment diagnostics
    const isLocalhost = window.location.hostname === 'localhost';
    const isNetlifyDev = window.location.hostname.includes('netlify.app') || 
                         window.location.hostname.includes('lovableproject.com');
    const isCustomDomain = !isLocalhost && !isNetlifyDev;
    
    console.error("Deployment context:", { 
      isLocalhost, 
      isNetlifyDev, 
      isCustomDomain,
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      functionPath: 'https://xjlpuyrqxgxnyixqwfml.supabase.co/functions/v1/process-form'
    });
    
    // If it starts with "<" it's likely HTML, not JSON
    if (errorText.trim().startsWith("<")) {
      console.error("Received HTML instead of JSON:", errorText.substring(0, 200) + "...");
      
      // Look for specific development errors like "Function Not Found"
      if (errorText.includes("Function Not Found") || errorText.includes("couldn't find function")) {
        throw new Error("Supabase function not found. Check that the function is deployed correctly.");
      }
      // Check for common error patterns in HTML responses
      else if (errorText.includes("404") || errorText.includes("not found")) {
        throw new Error("Function not found (404). Verify the function is deployed correctly.");
      } else if (errorText.includes("500") || errorText.includes("Internal Server Error")) {
        throw new Error("Server error (500). The function crashed. Check Supabase function logs.");
      } else if (errorText.includes("403") || errorText.includes("Forbidden")) {
        throw new Error("Access forbidden (403). Check CORS settings in your function configuration.");
      } else {
        throw new Error(`Server returned HTML instead of JSON (Status: ${response.status}). The function may have failed to initialize. Check Supabase function logs.`);
      }
    }
    
    // Otherwise try to parse as JSON if possible
    try {
      const errorJson = JSON.parse(errorText);
      const errorDetail = errorJson.error || errorJson.message || `Error ${response.status}`;
      throw new Error(`API Error: ${errorDetail}`);
    } catch (parseError) {
      // Specific error messages for common HTTP status codes
      if (response.status === 404) {
        throw new Error(`Function not found (404). There's a discrepancy between your path tests and form submission. Try inspecting the browser network tab to see the exact URL being requested.`);
      } else if (response.status === 500) {
        throw new Error("Server error (500). The function crashed during execution. Check Supabase function logs for details.");
      } else if (response.status === 403) {
        throw new Error("Access forbidden (403). Check CORS settings and make sure your domain is allowed to access the function.");
      }
      
      // If parsing fails, return the raw text with some helpful context
      throw new Error(`Error ${response.status}: ${errorText.substring(0, 100)}... | This likely indicates a configuration issue in your Supabase function.`);
    }
  }
  
  // Keep existing code for successful responses
  try {
    // Get the response as text first to validate
    const responseText = await response.text();
    
    console.log("Response status:", response.status);
    console.log("Response headers:", JSON.stringify(Array.from(response.headers.entries())));
    console.log("Response text (first 300 chars):", responseText.substring(0, 300));
    
    // Empty response check
    if (!responseText.trim()) {
      throw new Error("Empty response received from server. The Supabase function might be misconfigured or not returning any data.");
    }
    
    // Check if it's HTML (error page)
    if (responseText.trim().startsWith("<")) {
      console.error("Received HTML instead of JSON in success path:", responseText.substring(0, 200) + "...");
      throw new Error("Server returned HTML instead of JSON. This may indicate a Supabase function misconfiguration or routing issue.");
    }
    
    // Parse the text as JSON
    try {
      const jsonResponse = JSON.parse(responseText);
      
      // If there's an error message in the response
      if (jsonResponse.error) {
        throw new Error(`Server reported an error: ${jsonResponse.error}`);
      }
      
      // Check email submission results
      if (jsonResponse.data && jsonResponse.data.email) {
        const emailResult = jsonResponse.data.email;
        
        // Handle warning separately from failure
        if (emailResult.warning) {
          console.warn('Email warning:', emailResult.warning);
        }
        
        // More detailed success response with all available information
        return {
          success: true,
          message: emailResult.message || 'Form submitted successfully',
          warning: emailResult.warning,
          recipient: emailResult.recipient || emailResult.emailTo,
          subject: emailResult.subject,
          data: emailResult
        };
      }
      
      // Return a successful response
      return {
        success: true,
        message: 'Form submitted successfully',
        data: jsonResponse.data
      };
    } catch (jsonError) {
      console.error("Failed to parse JSON response:", jsonError);
      console.error("Raw response text:", responseText.substring(0, 200) + "...");
      throw new Error("Failed to parse server response as JSON. Response may be malformed. Check that your Supabase function is returning properly formatted JSON.");
    }
  } catch (error) {
    console.error('Error parsing Supabase function response:', error);
    throw error;
  }
};
